import type { AxiosError } from "axios";
import axios from "axios";
import { backend_url } from "../constants";
import moment from "moment";
import { AXIOS_CONFIG } from "./api";
// Define the expected data type with an index signature
type ExpectedDataType = {
  [key: string]: any; // This allows any string keys with any value type
};

export const fetchData = async (
  url: string,
  eventType: string,
  params?: any
) => {
  try {
    let returnData: { [key: string]: any } = {};
    const response = await axios.get<string>(`${backend_url}/${url}`, {
      params: {
        ...params,
      },
      ...AXIOS_CONFIG,
    });
    returnData["response"] = response.data;
    returnData["eventType"] = eventType;
    return returnData;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<ExpectedDataType>;
      if (axiosError.response) {
        console.error(
          "Error fetching data: ",
          axiosError.response.status,
          axiosError.response.data
        );
      } else if (axiosError.request) {
        console.error("Error fetching data: No response received");
      } else {
        console.error("Error fetching data: ", error.message);
      }
    } else {
      console.error("Error fetching data: ", error);
    }

    throw error;
  }
};

export function convertKeysToLowercase(obj: { [key: string]: any }) {
  const newObj: { [key: string]: any } = {};

  for (const key in obj) {
    if (Object.hasOwnProperty.call(obj, key)) {
      const lowercaseKey = key.toLowerCase();
      newObj[lowercaseKey] = obj[key];
    }
  }

  return newObj;
}

/**
 * Parses a JSON string into an object. If the string is null, undefined, empty,
 * or invalid JSON, returns an empty object as a default fallback.
 *
 * @param {string|null} jsonString The JSON string to parse.
 * @return {object} The parsed JSON object or an empty object on failure.
 */
export function parseJsonOrDefault(jsonString: string | null | undefined) {
  if (!jsonString || jsonString === "undefined") {
    return {}; // Return an empty object if input is null, undefined, or "undefined"
  }

  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error("Error parsing JSON:", error);
    return {}; // Return an empty object if JSON parsing fails
  }
}

/**
 * Returns an object containing placeholder data for the ID Vault.
 *
 * @param type The type of ID Vault (e.g. "Your", "Spouse", "Dependent")
 * @param {Object} personalInfo The personal information object
 * @returns {Object} An object containing placeholder data for the ID Vault
 * @throws {Error} If the type is not "Your", "Spouse", or "Dependent"
 */
export function getIdVaultPlaceholderData(
  type: string,
  {
    personalInfo,
    spouseInfo,
    spouseSensitiveInfo,
    dependentInfo,
    dependentSensitiveInfo,
    sensitiveInfo,
    depNum,
  }: any
) {
  if (type !== "Your" && type !== "Spouse" && type !== "Dependent") {
    throw new Error("Invalid ID Vault type");
  }
  switch (type) {
    case "Your":
      return {
        firstName: personalInfo?.firstName || "",
        lastName: personalInfo?.lastName || "",
        middleName: personalInfo?.middleName || "",
        dob: personalInfo?.dateOfBirth || "",
        ssn: (sensitiveInfo && (sensitiveInfo[0]?.data as string)) || "",
        bankAccountNumber:
          (sensitiveInfo && (sensitiveInfo[1]?.data as string)) || "",
        bankRoutingNumber:
          (sensitiveInfo && (sensitiveInfo[2]?.data as string)) || "",
        bankAccountType:
          (sensitiveInfo && (sensitiveInfo[3]?.data as string)) || "",
        ippin:
          (sensitiveInfo &&
            (sensitiveInfo[sensitiveInfo.length - 1]?.data as string)) ||
          "",
      };
    case "Spouse":
      return {
        firstName: spouseInfo?.firstname || "",
        lastName: spouseInfo?.lastname || "",
        middleName: spouseInfo?.middlename || "",
        dob: spouseSensitiveInfo?.spouseDob || "",
        ssn: spouseSensitiveInfo?.spouseSsn || "",
        ippin: spouseSensitiveInfo?.ippin || "",
      };
    case "Dependent":
      const dependent = dependentInfo[depNum - 1] || {};
      return {
        firstName: dependent?.firstname || "",
        lastName: dependent?.lastname || "",
        middleName: dependent?.middlename || "",
        ssn: dependentSensitiveInfo?.dependentSsn || "",
        dob: dependentSensitiveInfo?.dependentDob || "",
        ippin: "", // Assuming IP PIN isn't used for dependents
      };
    default:
      return {
        firstName: "",
        lastName: "",
        middleName: "",
        dob: "",
        ssn: "",
        ippin: "",
      };
  }
}

/**
 * Returns a formatted value based on the provided type.
 * @param {string} type - The type of value to format.
 * @param {string} value - The value to format.
 * @returns {string} The formatted value.
 */

export function formatValue(type: string, value: any) {
  if (value === null || value === undefined) return "";
  // Always coerce to string for string ops
  const strValue = typeof value === "string" ? value : String(value);
  switch (type) {
    case "date":
      return moment(strValue).format("MM/DD/YYYY");
    case "ssn":
      const numericOnly = strValue.replace(/\D/g, ""); // Strip all non-digits
      let formatted = "";
      // Slice and add dashes only when necessary
      if (numericOnly.length > 0) {
        formatted += numericOnly.slice(0, 3);
        if (numericOnly.length > 3) {
          formatted += "-" + numericOnly.slice(3, 5);
        }
        if (numericOnly.length > 5) {
          formatted += "-" + numericOnly.slice(5, 9);
        }
      }
      return formatted;
    case "ippin":
      return strValue.replace(/(\d{3})(\d{2})(\d{4})/, "$1-$2-$3");
    case "phone":
      const phoneNumber = strValue.replace(/\D/g, ""); // Strip all non-digits
      let formattedPhoneNumber = "";
      // Slice and add dashes only when necessary
      if (phoneNumber.length > 0) {
        formattedPhoneNumber += phoneNumber.slice(0, 3);
        if (phoneNumber.length > 3) {
          formattedPhoneNumber += "-" + phoneNumber.slice(3, 6);
        }
        if (phoneNumber.length > 6) {
          formattedPhoneNumber += "-" + phoneNumber.slice(6, 10);
        }
      }
      return formattedPhoneNumber;
    case "email":
      return strValue
        .replace(/\D/g, "")
        .replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
    case "routing":
      return strValue
        .replace(/\D/g, "")
        .replace(/(\d{3})(\d{3})(\d{3})/, "$1-$2-$3");
    case "account":
      return strValue
        .replace(/\D/g, "")
        .replace(/(\d{3})(\d{3})(\d{3})/, "$1-$2-$3");
    case "zip":
      return strValue.replace(/\D/g, "").replace(/(\d{5})(\d{4})/, "$1-$2");
    default:
      return strValue;
  }
}

/**
 * Sanitizes a filename by replacing invalid characters, collapsing repeated
 * underscores, and appending a timestamp. The filename is also trimmed of
 * leading/trailing whitespace and underscores. If the filename is empty after
 * extension removal, it is replaced with 'untitled'.
 *
 * @param {string} fileName The original filename to sanitize.
 * @returns {string} The sanitized filename.
 */
export function fileNameFormatter(fileName: string) {
  // Helper function to sanitize strings without vulnerable regex
  const sanitizeString = (input: string): string => {
    let sanitized = "";
    let prevWasUnderscore = false;

    // First pass: Remove invalid chars and collapse underscores
    for (const char of input) {
      // Check if the character is invalid
      const isInvalid =
        '<>:"/\\|?*'.includes(char) || // Special characters
        char.charCodeAt(0) <= 0x1f; // Control characters

      if (isInvalid) {
        // If the character is invalid, add it to the sanitized string
        if (!prevWasUnderscore) {
          sanitized += "_";
          prevWasUnderscore = true;
        }
      } else if (char === "_") {
        if (!prevWasUnderscore) {
          sanitized += "_";
          prevWasUnderscore = true;
        }
      } else {
        sanitized += char;
        prevWasUnderscore = false;
      }
    }

    // Second pass: Trim leading/trailing underscores
    return sanitized.replace(/^_+|_+$/g, "") || "untitled";
  };

  // Handle undefined/null/empty input
  const originalFileName = (fileName || "").trim() || "untitled";

  // Generate ISO timestamp without special characters
  const timeStamp = new Date().toISOString().replace(/[-:.TZ]/g, "");

  // Split filename into parts
  const parts = originalFileName.split(".");
  let fileExtension = "";
  let fileNameWithoutExt = originalFileName;

  // Process extension if present
  if (parts.length > 1) {
    const extPart = parts.pop()!.trim();
    if (extPart) {
      const sanitizedExt = sanitizeString(extPart).replace(/^untitled$/, ""); // Prevent "untitled" extension
      if (sanitizedExt) {
        fileExtension = `.${sanitizedExt}`;
      }
    }
    fileNameWithoutExt = parts.join(".").trim();
  }

  // Sanitize main filename
  let sanitizedName = sanitizeString(fileNameWithoutExt);

  // Final fallback for empty names
  if (!sanitizedName) {
    sanitizedName = "untitled";
  }

  // Construct final filename
  return `${sanitizedName}_${timeStamp}${fileExtension}`;
}

export const getFullNameError = (name: string) => {
  const specialCharacter = /[^a-zA-Z\s]/.test(name);
  if (specialCharacter) {
    return "Special characters are not allowed.";
  }
  return name.length < 3 && name.length > 0
    ? "Name must be at least 3 characters."
    : "";
};

/**
 * Lightens a hex color by a specified percentage.
 * @param {string} hex - The hex color code to lighten (e.g. "#FF0000")
 * @param {number} percent - The percentage to lighten by (0-1)
 * @returns {string} The lightened hex color code
 * @throws {Error} If the hex color is invalid or percent is out of range
 */
export function lightenHex(hex: string, percent: number): string {
  if (!hex.startsWith("#")) {
    throw new Error("Hex color must start with #");
  }

  if (percent < 0 || percent > 1) {
    throw new Error("Percent must be between 0 and 1");
  }

  const num = parseInt(hex.slice(1), 16);
  let r = (num >> 16) + Math.round((255 - (num >> 16)) * percent);
  let g =
    ((num >> 8) & 0x00ff) + Math.round((255 - ((num >> 8) & 0x00ff)) * percent);
  let b = (num & 0x0000ff) + Math.round((255 - (num & 0x0000ff)) * percent);

  r = Math.min(255, r);
  g = Math.min(255, g);
  b = Math.min(255, b);

  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}
